import { useCallback, useEffect, useRef, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  ExternalLink,
  FileSignature,
  FileText,
  RefreshCw,
  ShieldQuestion,
  Upload,
} from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import {
  useGetDriverDocumentUrl,
  useGetDriverDocuments,
  useImportDriverFormDocuments,
  useImportSignedContract,
  useOverrideDriverDocument,
  useReadDriverDocumentAgain,
  useUploadDriverDocument,
} from '../../hooks/backend/drivers'
import { writeErrorMessage } from '../../utils/writeError'

// The Documents block on a driver record.
//
// WHAT IT REPLACES. A section that has been on this screen since the port and
// has never shown anything real: it listed api.typeform.com URLs, which need a
// Typeform login to open, and for most drivers it listed nothing at all. Yolisa
// asked for the signed agreement, the licence and the ID on 27 August 2026.
//
// THREE WAYS A DOCUMENT GETS HERE, and the screen says which one each came from,
// because the provenance is the difference between a record and a rumour:
//
//   Fetch from SignNow   the signed employment contract, flattened, so the
//                        agreement can be read without a SignNow login.
//   Pull from the form   the certified ID, licence and proof of address the
//                        driver already uploaded on the contract onboarding
//                        form. Downloaded as OUR copy, because a Typeform URL
//                        dies with somebody else's account.
//   Upload               a manager files it himself. The only path that reaches
//                        the drivers already in the fleet, whose submissions
//                        never asked for these documents.
//
// EVERY VERDICT COMES FROM THE DATABASE. This component renders status,
// rejected_reason and can_override; it never decides any of them. In particular
// can_override is computed in v_driver_documents so this screen cannot offer a
// button the write function will refuse.

const MAX_BYTES = 15 * 1024 * 1024

const UPLOAD_KINDS = [
  { code: 'id_document', label: 'Certified copy of ID' },
  { code: 'drivers_licence', label: 'Driving licence and PrDP' },
  { code: 'proof_of_address', label: 'Proof of address' },
  { code: 'uber_profile', label: 'Uber profile screenshot' },
  { code: 'uber_earnings', label: 'Uber earnings screenshot' },
  { code: 'other', label: 'Something else' },
]

// The label a manager sees for a pending form file, matched against the same
// codes the upload picker above already uses. driver_document_form_files_v1
// classifies by matching the question's own title, not by asking anyone, so a
// question it cannot place lands on "other" here too.
function kindLabel(code: string): string {
  return UPLOAD_KINDS.find((k) => k.code === code)?.label ?? 'Something else'
}

type CoverageRow = {
  doc_kind: string
  label: string
  state: 'on_file' | 'pending' | 'rejected' | 'missing'
  expires_at: string | null
  is_certified_copy: boolean | null
  rejected_reason: string | null
}

type DetailLine = { label: string; value: string }

type DocRow = {
  id: string
  doc_kind: string
  kind_label: string
  // Built in Postgres from the driver's name ON RECORD plus what the document
  // is. Never from the name the reader saw: it read "HALE" off Steven Ofentse
  // Booi's licence card on 27 August 2026.
  title: string
  // An ordered [{label, value}] of what the document actually says, computed by
  // driver_document_detail_lines_v1. A licence has different fields from a bank
  // statement and this component knows about neither: it renders what it is
  // given. CLAUDE.md rule 4.
  detail_lines: DetailLine[] | null
  document_number: string | null
  issued_on: string | null
  certified_on: string | null
  certified_by: string | null
  original_filename: string | null
  source: 'manager_upload' | 'onboarding_form' | 'signnow'
  status: 'pending_read' | 'accepted' | 'rejected'
  rejected_class: string | null
  rejected_reason: string | null
  can_override: boolean
  was_overridden: boolean
  override_reason: string | null
  override_by_name: string | null
  read: Record<string, unknown> | null
  read_method: string | null
  is_certified_copy: boolean | null
  expires_at: string | null
  expiry_state: 'expired' | 'expiring' | 'valid' | null
  superseded: boolean
  uploaded_at: string | null
  uploaded_by_name: string | null
}

type PendingFormFile = {
  doc_kind: string
  question: string
  file_url: string
  source_ref: string
  source_form: string
}

type PanelData = {
  driver_name: string
  canManage: boolean
  coverage: CoverageRow[]
  documents: DocRow[]
  form_files_pending: number
  // Optional so an older cached response, or a test fixture that only sets
  // form_files_pending, still renders: the list is additive, the count is not
  // going away under it.
  form_files?: PendingFormFile[]
  contract_envelope: { envelope_id: string; signed_at: string | null; copy_on_file: boolean } | null
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : ''
      resolve(result.split(',')[1] ?? '')
    }
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file.'))
    reader.readAsDataURL(file)
  })
}

// 'onboarding_form' covers BOTH Typeforms since migration 0103: the contract
// onboarding form and the Driver Employment Form. The stored value was not
// renamed because it is on five rows in production and the word the operator
// reads is this label, not the enum.
const SOURCE_LABEL: Record<string, string> = {
  manager_upload: 'Uploaded by a manager',
  onboarding_form: "From the driver's own form submission",
  signnow: 'From SignNow',
}

function CoverageChip({ row }: { row: CoverageRow }) {
  const tone =
    row.state === 'on_file'
      ? 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200'
      : row.state === 'rejected'
        ? 'border-rose-300 bg-rose-50 text-rose-800 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-200'
        : row.state === 'pending'
          ? 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200'
          : 'border-border bg-muted/40 text-muted-foreground'
  const word =
    row.state === 'on_file'
      ? 'On file'
      : row.state === 'rejected'
        ? 'Rejected'
        : row.state === 'pending'
          ? 'Being read'
          : 'Missing'
  return (
    <div className={`rounded-md border px-2.5 py-1.5 text-xs ${tone}`}>
      <span className="font-medium">{row.label}</span>
      <span className="ml-1.5 opacity-80">{word}</span>
      {row.state === 'on_file' && row.is_certified_copy === false && (
        <span className="ml-1.5 opacity-80">not certified</span>
      )}
    </div>
  )
}

export function DriverDocumentsPanel({ applicationId }: { applicationId: string }) {
  const panel = useGetDriverDocuments<PanelData>()
  const upload = useUploadDriverDocument()
  const importForm = useImportDriverFormDocuments()
  const importContract = useImportSignedContract()
  const override = useOverrideDriverDocument()
  const readAgain = useReadDriverDocumentAgain()
  const getUrl = useGetDriverDocumentUrl()
  const inputRef = useRef<HTMLInputElement>(null)

  const trigger = panel.trigger
  const load = useCallback(() => {
    void trigger({ application_id: applicationId }, { skipCache: true })
  }, [trigger, applicationId])

  useEffect(() => {
    load()
  }, [load])

  const [kind, setKind] = useState(UPLOAD_KINDS[0].code)
  const [file, setFile] = useState<File | null>(null)
  const [tooLarge, setTooLarge] = useState(false)
  const [overrideFor, setOverrideFor] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState<string | null>(null)

  const data = panel.data
  const canManage = data?.canManage === true
  const current = (data?.documents ?? []).filter((d) => !d.superseded)
  const history = (data?.documents ?? []).filter((d) => d.superseded)

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const next = e.target.files?.[0] ?? null
    setFile(next)
    setTooLarge(next != null && next.size > MAX_BYTES)
  }

  async function handleUpload() {
    if (!file || tooLarge) return
    setBusy('upload')
    try {
      const contentBase64 = await readAsBase64(file)
      const res = (await upload.trigger({
        application_id: applicationId,
        doc_kind: kind,
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        contentBase64,
      }).result) as { verdict?: { status?: string; rejected_reason?: string } } | null
      const status = res?.verdict?.status
      // The verdict is reported as the verdict, not as success. A rejection is a
      // correct outcome of a successful upload and saying "Uploaded" would hide
      // the one thing the manager has to act on.
      if (status === 'rejected') {
        toast.error('Filed, but rejected on reading', {
          description: res?.verdict?.rejected_reason ?? undefined,
        })
      } else {
        toast.success('Document filed and accepted.')
      }
      setFile(null)
      if (inputRef.current) inputRef.current.value = ''
      load()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(null)
    }
  }

  async function handleImportForm() {
    setBusy('form')
    try {
      const res = (await importForm.trigger({ application_id: applicationId }).result) as
        | { imported?: number; skipped?: number; note?: string }
        | null
      if (res?.note) toast.info(res.note)
      else toast.success(`${res?.imported ?? 0} document(s) pulled in from the onboarding form.`)
      load()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(null)
    }
  }

  async function handleImportContract() {
    setBusy('contract')
    try {
      const res = (await importContract.trigger({ application_id: applicationId }).result) as
        | { already_on_file?: boolean }
        | null
      toast.success(
        res?.already_on_file
          ? 'The signed contract was already on file.'
          : 'Signed contract fetched from SignNow and filed.',
      )
      load()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(null)
    }
  }

  async function handleOpen(documentId: string) {
    try {
      const res = (await getUrl.trigger(
        { application_id: applicationId, document_id: documentId },
        { skipCache: true },
      ).result) as { url: string | null } | null
      if (res?.url) window.open(res.url, '_blank', 'noopener,noreferrer')
      else toast.error('That document has no viewable file.')
    } catch (err) {
      toast.error(writeErrorMessage(err))
    }
  }

  async function handleReadAgain(documentId: string) {
    setBusy(`read-${documentId}`)
    try {
      const res = (await readAgain.trigger({
        application_id: applicationId,
        document_id: documentId,
      }).result) as { verdict?: { status?: string; rejected_reason?: string } } | null
      if (res?.verdict?.status === 'rejected') {
        toast.error('Read again, still refused', {
          description: res?.verdict?.rejected_reason ?? undefined,
        })
      } else {
        toast.success('Read again and accepted.')
      }
      load()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(null)
    }
  }

  async function handleOverride(documentId: string) {
    setBusy(documentId)
    try {
      await override.trigger({
        application_id: applicationId,
        document_id: documentId,
        reason,
      }).result
      toast.success('Accepted, with your reason recorded against it.')
      setOverrideFor(null)
      setReason('')
      load()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(null)
    }
  }

  if (panel.loading && !data) {
    return (
      <section className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner className="h-4 w-4" /> Loading documents…
        </div>
      </section>
    )
  }

  if (panel.error) {
    return (
      <section className="rounded-lg border border-destructive/30 bg-destructive/10 p-4">
        <p className="text-sm text-destructive">{panel.error}</p>
      </section>
    )
  }

  const contract = data?.contract_envelope ?? null

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <div className="mb-3 flex items-center gap-2">
        <FileText className="h-4 w-4 text-[hsl(var(--brand))]" />
        <h3 className="text-sm font-semibold text-foreground">Documents</h3>
      </div>

      {/* What we are supposed to hold, and whether we hold it. Four chips rather
          than a list, so a gap is visible without reading. */}
      <div className="mb-4 flex flex-wrap gap-2">
        {(data?.coverage ?? []).map((c) => (
          <CoverageChip key={c.doc_kind} row={c} />
        ))}
      </div>

      {/* The two ways a document arrives without anybody photographing anything. */}
      {canManage && (
        <div className="mb-4 flex flex-wrap gap-2">
          {contract && !contract.copy_on_file && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleImportContract}
              disabled={busy !== null}
            >
              {busy === 'contract' ? (
                <Spinner className="mr-2 h-4 w-4" />
              ) : (
                <FileSignature className="mr-2 h-4 w-4" />
              )}
              Fetch signed contract from SignNow
            </Button>
          )}
          {(data?.form_files_pending ?? 0) > 0 && (
            <Button size="sm" variant="outline" onClick={handleImportForm} disabled={busy !== null}>
              {busy === 'form' ? (
                <Spinner className="mr-2 h-4 w-4" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Pull {data?.form_files_pending} file(s) from the onboarding form
            </Button>
          )}
        </div>
      )}

      {!contract && (
        <p className="mb-4 text-xs text-muted-foreground">
          No signed employment contract on record, so there is none to fetch. Send it from the
          Employment contract panel above.
        </p>
      )}

      {/* What the form is holding that we have not copied in yet, titled by its
          real question rather than by Typeform's file name. Visible to anyone
          who can see this screen; pulling it in, below, is still restricted to
          whoever can manage this driver's documents. */}
      {(data?.form_files?.length ?? 0) > 0 && (
        <div className="mb-3">
          <p className="mb-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
            Waiting on the form, not yet pulled in
          </p>
          <ul className="flex flex-col gap-1.5">
            {(data?.form_files ?? []).map((f) => (
              <li key={f.source_ref}>
                <a
                  href={f.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex max-w-full items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground transition-colors hover:bg-accent"
                >
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 truncate">
                    <span className="font-medium">{kindLabel(f.doc_kind)}</span>
                    <span className="ml-1.5 text-xs text-muted-foreground">{f.question}</span>
                  </span>
                  <ExternalLink className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Everything on file. */}
      {current.length === 0 ? (
        <p className="mb-4 text-sm text-muted-foreground">
          Nothing on file yet for this driver.
        </p>
      ) : (
        <ul className="mb-4 overflow-hidden rounded-md border border-border">
          {current.map((d, i) => (
            <li key={d.id} className={i > 0 ? 'border-t border-border' : undefined}>
              <div className="px-3 py-2.5">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleOpen(d.id)}
                    className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm text-foreground transition-colors hover:text-[hsl(var(--brand))]"
                  >
                    {d.status === 'accepted' ? (
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                    ) : d.status === 'rejected' ? (
                      <AlertTriangle className="h-4 w-4 shrink-0 text-rose-600" />
                    ) : (
                      <ShieldQuestion className="h-4 w-4 shrink-0 text-amber-600" />
                    )}
                    <span className="min-w-0 flex-1 truncate">{d.title}</span>
                    <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  </button>
                </div>

                {/* What the document says. Two columns of label and value, so a
                    manager can check a number against the card in his hand
                    without opening the file. Empty when nothing has read it. */}
                {d.detail_lines && d.detail_lines.length > 0 && (
                  <dl className="mt-1.5 grid gap-x-6 gap-y-0.5 text-xs sm:grid-cols-2">
                    {d.detail_lines.map((line) => (
                      <div key={line.label} className="flex gap-1.5">
                        <dt className="shrink-0 text-muted-foreground">{line.label}</dt>
                        <dd className="min-w-0 break-words text-foreground">{line.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}

                <p className="mt-1.5 text-[11px] text-muted-foreground">
                  {SOURCE_LABEL[d.source] ?? d.source}
                  {d.uploaded_by_name ? ` · ${d.uploaded_by_name}` : ''}
                  {d.original_filename ? ` · ${d.original_filename}` : ''}
                  {d.read_method === 'not_read' ? ' · issued by Fleetly, not read' : ''}
                </p>

                {/* Expiry. The Gemini gap review asked for this twice, four
                    months apart, and it existed nowhere in the system. */}
                {d.expiry_state === 'expired' && (
                  <p className="mt-1 text-xs text-rose-700 dark:text-rose-400">
                    Expired on {d.expires_at}. This driver should not be carrying passengers on it.
                  </p>
                )}
                {d.expiry_state === 'expiring' && (
                  <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                    Expires on {d.expires_at}, inside thirty days.
                  </p>
                )}

                {d.status === 'rejected' && (
                  <div className="mt-2 rounded-md border border-rose-300 bg-rose-50 p-2.5 text-xs text-rose-800 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-200">
                    <p>{d.rejected_reason}</p>
                    {!d.can_override && (
                      <p className="mt-1.5 font-medium">
                        This one cannot be overridden. Fix the driver record or file the right
                        document.
                      </p>
                    )}
                    {canManage && overrideFor !== d.id && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {d.can_override && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setOverrideFor(d.id)
                              setReason('')
                            }}
                          >
                            Accept it anyway
                          </Button>
                        )}
                        {/* OFFERED ON EVERY REJECTION, including the two that
                            cannot be overridden, and that is the point. A
                            re-read is not an override: it re-runs the same
                            rules against the same file, so it launders nothing.
                            It is the correct remedy for the actual cause of nine
                            of the twelve refusals in the 27 August backfill,
                            which was image quality, not a wrong document. Two
                            licences were refused because the reader misread
                            digits off a photocopy of a photocopy, and a misread
                            that happens to be a well-formed number is reported
                            as an identity mismatch, which is a false accusation
                            against a real driver. This button is how he gets out
                            of it without anybody deleting a record. */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReadAgain(d.id)}
                          disabled={busy !== null}
                        >
                          {busy === `read-${d.id}` ? (
                            <Spinner className="mr-2 h-4 w-4" />
                          ) : (
                            <RefreshCw className="mr-2 h-4 w-4" />
                          )}
                          Read it again
                        </Button>
                      </div>
                    )}
                    {d.can_override && canManage && overrideFor === d.id && (
                      <div className="mt-2 space-y-2">
                        <Label htmlFor={`ov-${d.id}`} className="text-xs">
                          What can you see that the reader could not?
                        </Label>
                        <Input
                          id={`ov-${d.id}`}
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          placeholder="It is his green ID book, photographed at an angle."
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleOverride(d.id)}
                            disabled={reason.trim().length < 10 || busy === d.id}
                          >
                            {busy === d.id && <Spinner className="mr-2 h-4 w-4" />}
                            Record and accept
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setOverrideFor(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {d.was_overridden && (
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Accepted by {d.override_by_name ?? 'a manager'} against the reading:{' '}
                    {d.override_reason}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Upload. */}
      {canManage ? (
        <div className="flex flex-wrap items-end gap-3 border-t border-border pt-3">
          <div className="sm:w-56">
            <Label htmlFor="dd-kind" className="text-xs">
              What is it
            </Label>
            <NativeSelect
              id="dd-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="h-9"
            >
              {UPLOAD_KINDS.map((k) => (
                <option key={k.code} value={k.code}>
                  {k.label}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="min-w-0 flex-1">
            <Label htmlFor="dd-file" className="text-xs">
              File
            </Label>
            <input
              id="dd-file"
              ref={inputRef}
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp,image/heic"
              onChange={handleFile}
              className="mt-1 block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border file:border-border file:bg-background file:px-3 file:py-1.5 file:text-sm file:text-foreground"
            />
            {tooLarge && (
              <p className="mt-1 text-xs text-destructive">That file is larger than 15 MB.</p>
            )}
          </div>
          <Button size="sm" onClick={handleUpload} disabled={!file || tooLarge || busy !== null}>
            {busy === 'upload' ? (
              <Spinner className="mr-2 h-4 w-4" />
            ) : (
              <Upload className="mr-2 h-4 w-4" />
            )}
            File and read
          </Button>
        </div>
      ) : (
        <p className="border-t border-border pt-3 text-xs text-muted-foreground">
          You can see this driver&rsquo;s documents but not file new ones. That driver&rsquo;s own
          manager can, and so can anyone who holds Edit drivers.
        </p>
      )}

      {history.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-muted-foreground">
            {history.length} superseded document(s)
          </summary>
          <ul className="mt-2 space-y-1">
            {history.map((d) => (
              <li key={d.id} className="text-xs text-muted-foreground">
                <button
                  type="button"
                  onClick={() => handleOpen(d.id)}
                  className="underline underline-offset-2 hover:text-foreground"
                >
                  {d.title} · {d.uploaded_at?.slice(0, 10)}
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  )
}
